import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { Control, DropdownMenu } from '@adsk/alloy-react-dropdown';
import { PencilIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { InitialTime, TIME_DROPDOWN_ID, TimePickerTime } from './consts';
import useTimePicker from './hooks/useTimePicker';
import { UseTimePickerControlProps } from './hooks/useTimePickerControl';
import TimePickerControl from './TimePickerControl';

export type TimePickerProps = Omit<
  StylableComponent<HTMLDivElement>,
  'onChange'
> &
  Omit<UseTimePickerControlProps, 'initialTime'> & {
    initialTime?: InitialTime;
    error?: boolean;
    selectIncrement?: number;
    inlineEdit?: boolean;
    onEditDone?: (value?: TimePickerTime | undefined) => void;
    value?: InitialTime;
  };

const StyledPencilIcon = styledC(PencilIcon)`
  width: 17px;
  margin-right: 5px;
  color: ${theme.tokens.colors.icon.hover.value};
`;

const renderNothing = () => null;

/**
 * A TimePicker
 * Time pickers allow the selection of specific time. It displays a default text field where user can adjust hours, minutes and format.
 */
const TimePicker = React.forwardRef<HTMLDivElement, TimePickerProps>(
  (
    {
      error,
      disabled,
      selectIncrement = 30,
      initialTime = { hours: 0, minutes: 0 },
      inlineEdit,
      is12Hour,
      onChange,
      onFocus,
      onBlur,
      style,
      onEditDone,
      ...props
    },
    ref,
  ) => {
    const {
      forceRemount,
      edit,
      selectedTime,
      containerRef,
      interactiveStyles,
      controlProps,
      menuProps,
      controlOnChange,
    } = useTimePicker({
      disabled,
      is12Hour,
      selectIncrement,
      inlineEdit,
      initialTime,
      onChange,
      onFocus,
      onBlur,
      onEditDone,
      value: props.value,
    });

    const timeStyle = useMemo(
      () => [style, { border: 'none', paddingRight: '0' }],
      [style],
    );

    const renderTime = useCallback(() => {
      return (
        <TimePickerControl
          error={error}
          key={forceRemount}
          ref={ref}
          disabled={disabled}
          style={[
            ...timeStyle,
            inlineEdit && !edit ? { pointerEvents: 'none' } : undefined,
            inlineEdit ? { paddingLeft: '5px' } : undefined,
          ]}
          initialTime={selectedTime}
          is12Hour={is12Hour}
          onChange={controlOnChange}
          onEditDone={onEditDone}
          {...props}
        />
      );
    }, [
      error,
      forceRemount,
      ref,
      disabled,
      timeStyle,
      inlineEdit,
      edit,
      selectedTime,
      is12Hour,
      controlOnChange,
      onEditDone,
      props,
    ]);

    const handleIconClick = useCallback(() => {
      controlProps?.ref?.current?.focus();
      controlProps?.ref?.current?.click();
    }, [controlProps]);

    const hoverBorderStyle = useMemo(() => {
      let borderStyle;
      if (error) {
        borderStyle = `1px solid ${theme.tokens.colors.border.error.value}`;
      } else if (disabled) {
        borderStyle = 'none';
      } else {
        borderStyle = `1px dashed ${theme.tokens.colors.border.hover.value}`;
      }

      return {
        border: borderStyle,
      };
    }, [error, disabled]);

    return (
      <div
        ref={containerRef}
        css={
          inlineEdit &&
          !edit && {
            display: 'flex',
            alignItems: 'center',
            borderRadius: theme.borderRadius.small,
            border: '1px solid',
            borderColor: error
              ? theme.tokens.colors.border.error.value
              : 'transparent',
            interactiveStyles,
            '&:hover': {
              ...hoverBorderStyle,
              cursor: 'pointer',
              '> svg': {
                visibility: 'visible',
              },
            },
            '> svg': {
              visibility: 'hidden',
            },
            button: {
              display: 'none',
              visibility: 'hidden',
            },
          }
        }
      >
        <Control
          id={TIME_DROPDOWN_ID}
          {...controlProps}
          renderInput={renderTime}
          renderPlaceholder={() => null}
          renderItem={() => null}
          disabled={disabled}
          style={[
            {
              padding: 0,
              border: inlineEdit && !edit ? 'none' : undefined,
              ...(error && {
                borderColor: theme.tokens.colors.border.error.value,
              }),
            },
            inlineEdit && !edit
              ? {
                  '&:hover': { boxShadow: 'none' },
                  '&:active': { boxShadow: 'none' },
                }
              : undefined,
          ]}
          highDensity
          highlightButtonOnFocus
        />
        <DropdownMenu {...menuProps} renderFooter={renderNothing} />
        {inlineEdit && !disabled && !edit && (
          <StyledPencilIcon onClick={handleIconClick} />
        )}
      </div>
    );
  },
);

TimePicker.displayName = 'TimePicker';

TimePicker.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The initial time */
  initialTime: PropTypes.shape({
    hours: PropTypes.number.isRequired,
    minutes: PropTypes.number.isRequired,
  }),
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** callback when selected value changed */
  onChange: PropTypes.func,
  /** callback when focus is lost */
  onBlur: PropTypes.func,
  /** callback when focus is gained */
  onFocus: PropTypes.func,
  /** callback when time has been entered in each field of manual entry or selection in the dropdown */
  onEditDone: PropTypes.func,
  /** Override for 12hr or 24hr display.  Uses browser locale to determine this by default */
  is12Hour: PropTypes.bool,
  /** Select minutes increment for dropdown values */
  selectIncrement: PropTypes.oneOf([5, 10, 15, 30]),
  /** Inline edit mode for tables and compact interfaces */
  inlineEdit: PropTypes.bool,
};

export default TimePicker;
